import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, FileText, FolderOpen, Gauge, Phone, ArrowRight } from "lucide-react";
import { logError } from "@/lib/log";

const STEPS = [
  { icon: Sparkles, title: "Ask the AI Compliance Assistant", body: "Get instant, referenced answers on CQC and Ofsted regulations. When something needs a human, one tap sends it to our compliance specialist.", cta: "Open the assistant", to: "/assistant" },
  { icon: FileText, title: "Your policies & resources", body: "Your starter policy pack is included with your subscription. Browse the store for full packages, the start-up toolkit and mock interviews.", cta: "Browse resources", to: "/resources" },
  { icon: FolderOpen, title: "Build your evidence folder", body: "Save transcripts, interview reports, audits and uploads under each key question so you walk into inspection prepared.", cta: "Open evidence folder", to: "/evidence" },
  { icon: Gauge, title: "Track your readiness score", body: "Your dashboard blends the weekly checklist, mock interviews, policy coverage and staff training into one score with next actions.", cta: "See my score", to: "/dashboard" },
  { icon: Phone, title: "Your direct line", body: "Subscribers get a direct number and can book a call any weekday — real support from someone who knows your sector.", cta: "Finish tour", to: "/dashboard" },
];

export default function OnboardingTour() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => { if (user && !user.onboarding_done && user.role !== "admin") setOpen(true); }, [user]);

  const finish = async (to) => {
    setOpen(false);
    try { await api.post("/me/onboarding-done"); refresh?.(); } catch (err) { logError("Failed to mark onboarding done", err); }
    if (to && to !== "/dashboard") navigate(to);
  };

  const s = STEPS[step];
  const last = step === STEPS.length - 1;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
      <DialogContent className="max-w-md" data-testid="onboarding-tour">
        <div className="pt-2">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4"><s.icon className="w-6 h-6" /></div>
          <p className="text-xs uppercase tracking-[0.15em] text-accent font-bold mb-1">Welcome · step {step + 1} of {STEPS.length}</p>
          <DialogTitle className="font-display text-xl font-bold mb-2" data-testid="onboarding-title">{s.title}</DialogTitle>
          <p className="text-sm text-muted-foreground">{s.body}</p>
          <div className="flex gap-1.5 mt-5" data-testid="onboarding-progress">
            {STEPS.map((st, i) => <span key={st.title || st.body} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-primary" : "w-1.5 bg-border"}`} />)}
          </div>
          <div className="flex items-center justify-between mt-6">
            <Button variant="ghost" size="sm" onClick={() => finish()} data-testid="onboarding-skip">Skip tour</Button>
            <div className="flex gap-2">
              {step > 0 && <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} data-testid="onboarding-back">Back</Button>}
              {last ? (
                <Button size="sm" onClick={() => finish(s.to)} data-testid="onboarding-finish">{s.cta}</Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => finish(s.to)} data-testid="onboarding-cta">{s.cta}</Button>
                  <Button size="sm" onClick={() => setStep(step + 1)} data-testid="onboarding-next">Next <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
